import React, { useState, ChangeEvent, useCallback, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { MatchAdmin } from '../../../models/interfaces/Match';
import { MatchEntryAdmin } from '../../../models/interfaces/MatchEntry';
import { CapNumber, RosterEntry } from '../../../models/interfaces/RosterEntry';
import { SportProvider } from '../../../models/interfaces/SportProvider';
import ChangeCapTeamCol from './ChangeCapTeamCol';
import MatchEntryRosterRow from './MatchEntryRosterRow';

interface Props {
    matchAdmin: MatchAdmin;
    lightCapPlayerList: RosterEntry[] | undefined;
    triggerUpdatedLightCapPlayerList: () => void;
    darkCapPlayerList: RosterEntry[] | undefined;
    triggerUpdatedDarkCapPlayerList: () => void;
    sportProvider: SportProvider;
}

export interface ChangePlayerCapProps {
    player: RosterEntry;
    newCapNumber: CapNumber;
}

const MatchPlayerView = ({
    matchAdmin,
    lightCapPlayerList,
    triggerUpdatedLightCapPlayerList,
    darkCapPlayerList,
    triggerUpdatedDarkCapPlayerList,
    sportProvider,
}: Props) => {
    const [lightCapTeamChangePlayerCap, setLightCapTeamChangePlayerCap] =
        useState<ChangePlayerCapProps | undefined>();
    const [darkCapTeamChangePlayerCap, setDarkCapTeamChangePlayerCap] =
        useState<ChangePlayerCapProps | undefined>();
    const [submitting, setSubmitting] = useState(false);

    const [lightCapAdmin, setLightCapAdmin] = useState<MatchEntryAdmin | undefined>()
    const [darkCapAdmin, setDarkCapAdmin] = useState<MatchEntryAdmin | undefined>()

    useEffect(() => {
        matchAdmin.match.lightCapTeam.adminForUser(matchAdmin.user).then(matchEntryAdmin => {
            setLightCapAdmin(matchEntryAdmin)
        }).catch(() => {
            setLightCapAdmin(undefined)
        })
    }, [matchAdmin.match.lightCapTeam.id, matchAdmin.user.id])

    useEffect(() => {
        matchAdmin.match.darkCapTeam.adminForUser(matchAdmin.user).then(matchEntryAdmin => {
            setDarkCapAdmin(matchEntryAdmin)
        }).catch(() => {
            setDarkCapAdmin(undefined)
        })
    }, [matchAdmin.match.darkCapTeam.id, matchAdmin.user.id])

    const handleCapChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>, team: 'light' | 'dark', player: RosterEntry) => {
            const newCapNumber = sportProvider.playerCapNumbers.find(
                (capNumber) => capNumber.databaseValue === e.currentTarget.value
            );
            if (newCapNumber) {
                if (team === 'light') {
                    setLightCapTeamChangePlayerCap({ player, newCapNumber });
                } else if (team === 'dark') {
                    setDarkCapTeamChangePlayerCap({ player, newCapNumber });
                }
            }
        },
        [sportProvider.playerCapNumbers]
    );

    const cancelCapChange = useCallback((team: 'light' | 'dark') => {
        if (team === 'light') {
            setLightCapTeamChangePlayerCap(undefined);
        } else if (team === 'dark') {
            setDarkCapTeamChangePlayerCap(undefined);
        }
    }, []);

    const handleSubmit = useCallback(
        (team: 'light' | 'dark') => {
            setSubmitting(true);
            if (team === 'light' && lightCapTeamChangePlayerCap) {
                matchAdmin
                    .setCapChangeForPlayer(
                        lightCapTeamChangePlayerCap.newCapNumber,
                        lightCapTeamChangePlayerCap.player
                    )
                    .catch((error) => {
                        console.error({ error });
                    })
                    .finally(() => {
                        setLightCapTeamChangePlayerCap(undefined);
                        setSubmitting(false);
                        triggerUpdatedLightCapPlayerList();
                    });
            } else if (team === 'dark' && darkCapTeamChangePlayerCap) {
                matchAdmin
                    .setCapChangeForPlayer(
                        darkCapTeamChangePlayerCap.newCapNumber,
                        darkCapTeamChangePlayerCap.player
                    )
                    .catch((error) => {
                        console.error({ error });
                    })
                    .finally(() => {
                        setDarkCapTeamChangePlayerCap(undefined);
                        setSubmitting(false);
                        triggerUpdatedDarkCapPlayerList();
                    });
            }
        },
        [
            darkCapTeamChangePlayerCap,
            lightCapTeamChangePlayerCap,
            matchAdmin,
            triggerUpdatedLightCapPlayerList,
            triggerUpdatedDarkCapPlayerList,
        ]
    );

    return (
        <div className="MatchPlayerView">
            <Row>
                <Col>
                    <Row className="my-2">
                        <Col className='text-center'>
                            <h5 style={{ textTransform: 'capitalize' }}>{`Light Caps - ${matchAdmin.match.lightCapTeam.name}`}</h5>
                        </Col>
                    </Row>
                    { lightCapAdmin && <MatchEntryRosterRow sportProvider={sportProvider} matchEntryAdmin={lightCapAdmin} />}
                    <ChangeCapTeamCol
                        teamName={matchAdmin.match.lightCapTeam.name}
                        changePlayerCapProps={lightCapTeamChangePlayerCap}
                        team={'light'}
                        cancelCapChange={cancelCapChange}
                        handleSubmit={handleSubmit}
                        submitting={submitting}
                        playerList={lightCapPlayerList}
                        handleCapChange={handleCapChange}
                        sportProvider={sportProvider}
                    />
                </Col>
                <Col>
                    <Row className="my-2">
                        <Col className='text-center'>
                            <h5 style={{ textTransform: 'capitalize' }}>{`Dark Caps - ${matchAdmin.match.darkCapTeam.name}`}</h5>
                        </Col>
                    </Row>
                    { darkCapAdmin && <MatchEntryRosterRow sportProvider={sportProvider} matchEntryAdmin={darkCapAdmin} />}
                    <ChangeCapTeamCol
                        teamName={matchAdmin.match.darkCapTeam.name}
                        changePlayerCapProps={darkCapTeamChangePlayerCap}
                        team={'dark'}
                        cancelCapChange={cancelCapChange}
                        handleSubmit={handleSubmit}
                        submitting={submitting}
                        playerList={darkCapPlayerList}
                        handleCapChange={handleCapChange}
                        sportProvider={sportProvider}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default MatchPlayerView;
